import { Fragment, useState, useEffect, useRef, useCallback, type RefObject } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { HistoryGapMarker } from "@/components/animals/HistoryGapMarker";
import { HighlightInteractionButton } from "@/components/interactions/HighlightInteractionButton";
import {
  getDateGroupLabel,
  getDateGroupLabelLong,
  getScoreBandBadgeClass,
} from "@/lib/activity-event-utils";
import { FlagReason } from "@/lib/interaction-types";
import { ActiveFilterChip } from "@/components/ui/active-filter-chip";

export interface Interaction {
  id: string;
  animalId: string;
  animalName: string | null;
  locationCode: string;
  zoneCode: string | null;
  eventType: string;
  scoreCode: string | null;
  scoreBand: string | null;
  occurredAt: string;
  author: string | null;
  rawNote: string | null;
  kennelStatusNorm?: string | null;
  evaluationStatus?: string | null;
  sourceReportId?: string | null;
  sourceEmailId?: string | null;
  isFlagged?: boolean;
  flagReasons?: FlagReason[] | null;
}

interface InteractionsResponse {
  interactions: Interaction[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

const INTERACTION_PAGE_SIZE = 20;

function formatDateUTC(dateStr: string, formatStr: string = "MMM d, yyyy h:mm a"): string {
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(dateStr);
  const date = new Date(isDateOnly ? dateStr + "T00:00:00Z" : (dateStr.endsWith("Z") ? dateStr : dateStr + "Z"));
  if (isNaN(date.getTime())) return dateStr;
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const month = months[date.getUTCMonth()];
  const day = date.getUTCDate();
  const year = date.getUTCFullYear();
  if (formatStr === "MMM d, yyyy") return `${month} ${day}, ${year}`;
  const hours = date.getUTCHours();
  const mins = date.getUTCMinutes().toString().padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  const hour12 = hours % 12 || 12;
  return `${month} ${day}, ${year} ${hour12}:${mins} ${ampm}`;
}

function scoreBandFromCode(code: string): string | null {
  if (code === "1" || code === "2") return "GREEN";
  if (code === "3A" || code === "3B") return "YELLOW";
  if (["4A", "4B", "5A", "5B"].includes(code)) return "RED";
  return null;
}

function InteractionScoreDisplay({ scoreCode, scoreBand }: { scoreCode: string | null; scoreBand: string | null }) {
  if (!scoreCode) return <span className="text-muted-foreground">–</span>;
  return (
    <span className="flex items-center gap-1">
      <span className="font-medium">{scoreCode}</span>
      {scoreBand && (
        <Badge
          variant="outline"
          className={`text-xs px-1.5 py-0 h-5 ${getScoreBandBadgeClass(scoreBand)}`}
        >
          {scoreBand}
        </Badge>
      )}
    </span>
  );
}

function buildInteractionUrl(
  animalId: string,
  page: number,
  limit: number,
  scoreFilter: string | null,
  unscoredOnly: boolean,
  flagType: string | null,
  searchTerm?: string,
): string {
  const params = new URLSearchParams({
    animalId,
    startDate: "2000-01-01",
    endDate: "2100-12-31",
    limit: String(limit),
    page: String(page),
    sortBy: "occurred_at",
    sortDir: "desc",
  });
  if (scoreFilter) params.set("scoreCode", scoreFilter);
  if (unscoredOnly) params.set("unscored", "true");
  if (flagType) params.set("flagTypes", flagType);
  if (searchTerm?.trim()) params.set("search", searchTerm.trim());
  return `/api/interactions?${params}`;
}

export interface AnimalInteractionTimelineProps {
  animalId: string | undefined;
  activeScoreFilter: string | null;
  onScoreClear: () => void;
  activeUnscoredOnly: boolean;
  onUnscoredClear: () => void;
  activeFlagType: string | null;
  onFlagTypeChange: (val: string | null) => void;
  dataStartDate?: string | null;
  intakeDate?: string | null;
  onInteractionClick?: (interaction: Interaction) => void;
  scrollContainerRef?: RefObject<HTMLDivElement | null>;
  noteSearch: string;
}

export function AnimalInteractionTimeline({
  animalId,
  activeScoreFilter,
  onScoreClear,
  activeUnscoredOnly,
  onUnscoredClear,
  activeFlagType,
  onFlagTypeChange,
  dataStartDate,
  intakeDate,
  onInteractionClick,
  scrollContainerRef,
  noteSearch,
}: AnimalInteractionTimelineProps) {
  const safeAnimalId = animalId ?? "";
  const [interactionPage, setInteractionPage] = useState(1);
  const [extraInteractions, setExtraInteractions] = useState<Interaction[]>([]);
  const [exhausted, setExhausted] = useState(false);
  const [debouncedNoteSearch, setDebouncedNoteSearch] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedNoteSearch(noteSearch), 300);
    return () => clearTimeout(timer);
  }, [noteSearch]);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const loadingMoreRef = useRef(false);

  useEffect(() => {
    setInteractionPage(1);
    setExtraInteractions([]);
    setExhausted(false);
  }, [activeScoreFilter, activeUnscoredOnly, activeFlagType, noteSearch]);

  const { data, isLoading } = useQuery<InteractionsResponse>({
    queryKey: ["/api/interactions", safeAnimalId, "timeline", activeScoreFilter, activeUnscoredOnly, activeFlagType, debouncedNoteSearch],
    queryFn: async () => {
      const res = await fetch(
        buildInteractionUrl(safeAnimalId, 1, INTERACTION_PAGE_SIZE, activeScoreFilter, activeUnscoredOnly, activeFlagType, debouncedNoteSearch),
        { credentials: "include" },
      );
      if (!res.ok) throw new Error("Failed to fetch interactions");
      return res.json();
    },
    enabled: !!safeAnimalId,
    placeholderData: keepPreviousData,
  });

  const loadedInteractions = [...(data?.interactions ?? []), ...extraInteractions];
  const hasMore = !exhausted && loadedInteractions.length < (data?.pagination.total ?? 0);

  const handleLoadMore = useCallback(async () => {
    if (loadingMoreRef.current || !hasMore) return;
    loadingMoreRef.current = true;
    try {
      const nextPage = interactionPage + 1;
      const res = await fetch(
        buildInteractionUrl(safeAnimalId, nextPage, INTERACTION_PAGE_SIZE, activeScoreFilter, activeUnscoredOnly, activeFlagType, debouncedNoteSearch),
        { credentials: "include" },
      );
      const more: InteractionsResponse = await res.json();
      if (more.interactions.length === 0) {
        setExhausted(true);
      } else {
        setExtraInteractions((prev) => [...prev, ...more.interactions]);
        setInteractionPage(nextPage);
      }
    } finally {
      loadingMoreRef.current = false;
    }
  }, [safeAnimalId, interactionPage, hasMore, activeScoreFilter, activeUnscoredOnly, activeFlagType, debouncedNoteSearch]);

  useEffect(() => {
    const container = scrollContainerRef?.current;
    if (!container) return;
    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      if (scrollHeight - scrollTop - clientHeight < 120) handleLoadMore();
    };
    handleScroll();
    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => container.removeEventListener("scroll", handleScroll);
  }, [handleLoadMore, scrollContainerRef]);

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }

  const hasActiveFilter = !!activeFlagType || !!activeScoreFilter || activeUnscoredOnly || !!noteSearch.trim();
  const isEmpty = !loadedInteractions.length;

  let lastDateLabel = "";

  return (
    <div className="divide-y list-zebra">
      {(activeScoreFilter || activeUnscoredOnly) && (
        <div className="flex items-center gap-2 px-3 py-2.5 border-b">
          <span className="text-xs text-muted-foreground">Viewing:</span>
          {activeScoreFilter && (
            <Badge variant="secondary" className="gap-1.5 pl-2 pr-1">
              <InteractionScoreDisplay
                scoreCode={activeScoreFilter}
                scoreBand={scoreBandFromCode(activeScoreFilter)}
              />
              <button
                onClick={onScoreClear}
                className="rounded-sm opacity-70 hover:opacity-100 ml-0.5"
                data-testid="chip-clear-score-filter"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
          {activeUnscoredOnly && (
            <ActiveFilterChip
              label="Unscored only"
              onClear={onUnscoredClear}
              data-testid="chip-clear-unscored-filter"
            />
          )}
        </div>
      )}
      {isEmpty ? (
        <p className="text-sm text-muted-foreground py-4 text-center" data-testid="text-no-interactions">
          {hasActiveFilter ? "No interactions match this filter." : "No interactions recorded yet."}
        </p>
      ) : (
        <>
          {loadedInteractions.map((interaction) => {
            const dateLabel = getDateGroupLabel(new Date(interaction.occurredAt));
            const showHeader = dateLabel !== lastDateLabel;
            lastDateLabel = dateLabel;
            return (
              <Fragment key={interaction.id}>
                {showHeader && (
                  <div
                    className="px-3 py-1 text-[10px] font-medium text-muted-foreground/60 bg-muted/40 border-b"
                    data-testid={`date-group-${dateLabel}`}
                  >
                    <span className="sm:hidden">{dateLabel}</span>
                    <span className="hidden sm:inline">{getDateGroupLabelLong(new Date(interaction.occurredAt))}</span>
                  </div>
                )}
                <div
                  className="p-3 space-y-2 cursor-pointer hover-elevate transition-colors"
                  data-testid={`interaction-item-${interaction.id}`}
                  onClick={() => onInteractionClick?.(interaction)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onInteractionClick?.(interaction);
                    }
                  }}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs text-muted-foreground" data-testid={`interaction-date-${interaction.id}`}>
                        {formatDateUTC(interaction.occurredAt)}
                      </span>
                      <Badge variant="outline" className="text-xs">
                        {interaction.eventType === "BEH_ALERT" ? "Alert" : "Observation"}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <InteractionScoreDisplay
                        scoreCode={interaction.scoreCode}
                        scoreBand={interaction.scoreBand}
                      />
                      <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                        <HighlightInteractionButton
                          variant="icon"
                          interactionId={interaction.id}
                          isHighlighted={(interaction.flagReasons ?? []).some((r) => r.type === "highlighted")}
                          animalId={safeAnimalId}
                        />
                      </div>
                    </div>
                  </div>
                  {interaction.author && (
                    <p className="text-xs text-muted-foreground">by {interaction.author}</p>
                  )}
                  {interaction.rawNote ? (
                    <span className="text-sm text-muted-foreground line-clamp-2">{interaction.rawNote}</span>
                  ) : (
                    <span className="text-sm text-muted-foreground">–</span>
                  )}
                </div>
              </Fragment>
            );
          })}
          {hasMore ? (
            <div ref={sentinelRef} className="h-px" />
          ) : (
            <>
              <div ref={sentinelRef} />
              {dataStartDate && intakeDate && new Date(intakeDate) < new Date(dataStartDate) && (
                <HistoryGapMarker dataStartDate={dataStartDate} />
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
